// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Camera (and optional mic) lifecycle. The stream is the sensor input; frames are
 * diffed and discarded. Nothing is recorded unless motion triggers a clip. Every
 * track is stopped on teardown so the OS camera/mic indicator goes out.
 */

export interface CameraOptions {
  facing: 'user' | 'environment';
  audio: boolean;
}

/** Start the camera. Must be called from within a user gesture (iOS). */
export async function startCamera(video: HTMLVideoElement, opts: CameraOptions): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser does not expose a camera to web pages.');
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: opts.facing }, width: { ideal: 1280 }, height: { ideal: 720 } },
    audio: opts.audio
      ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
      : false,
  });
  video.srcObject = stream;
  video.setAttribute('playsinline', '');
  video.muted = true;
  await video.play().catch(() => {
    /* autoplay quirks — frames still render once metadata is ready */
  });
  return stream;
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
}

/** True once every track in the stream has fully stopped. */
export function allTracksEnded(stream: MediaStream | null): boolean {
  if (!stream) return true;
  return stream.getTracks().every((t) => t.readyState === 'ended');
}

/** List available video input labels once permission has been granted. */
export async function hasMultipleCameras(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'videoinput').length > 1;
  } catch {
    return false;
  }
}
